import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { fortiwebAPI } from '../lib/api';
import { STATUS } from '../lib/theme';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { LoadingSpinner } from '../components/LoadingSpinner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Pencil,
  Trash2,
  ShieldCheck,
  RefreshCw,
  Server,
  Search,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const PER_PAGE = 5;

// ── helpers ──────────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  agent_id: '',
  nama_jurnal: '',
  domain: '',
  ip_public: '',
  vip_fortiweb: '',
  ip_private: '',
  lewat_forti: 'Ya',
  pointing_to_vip: 'Sudah',
  update_ssl: 'Sudah',
  keterangan: '',
};

function StatusBadgeLocal({ value, positiveValues = ['ya', 'sudah'] }) {
  const isPositive = positiveValues.includes((value || '').toLowerCase());
  const colors = isPositive ? STATUS.valid : STATUS.critical;
  return (
    <Badge
      variant="outline"
      style={{ background: colors.bg, borderColor: colors.border, color: colors.text }}
    >
      {value || '-'}
    </Badge>
  );
}

// ── Komponen Utama ────────────────────────────────────────────────────────────
export function FortiwebPage() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Search & Pagination
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState('all');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  // Hapus
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ── filtered & paginated ─────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return rows.filter((r) => {
      const matchSearch = !q || [r.nama_jurnal, r.domain, r.ip_public, r.vip_fortiweb, r.ip_private, r.keterangan]
        .some((v) => (v || '').toLowerCase().includes(q));
      const matchStatus = filterStatus === 'all' ||
        (filterStatus === 'no_status' ? !r.keterangan : (r.keterangan || '') === filterStatus);
      return matchSearch && matchStatus;
    });
  }, [rows, search, filterStatus]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  // Set agent_id yang sudah dipakai (exclude agent milik row yang sedang diedit)
  const usedAgentIds = useMemo(() => {
    const editingId = editTarget?.agent_id;
    return new Set(
      rows
        .filter((r) => r.agent_id && r.agent_id !== editingId)
        .map((r) => r.agent_id)
    );
  }, [rows, editTarget]);

  // reset ke halaman 1 saat search atau filter berubah
  useEffect(() => { setPage(1); }, [search, filterStatus]);

  // ── load data ────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rowsRes, agentsRes] = await Promise.all([
        fortiwebAPI.getAll(),
        fortiwebAPI.getAgents(),
      ]);

      setRows(rowsRes.data.data);
      setAgents(agentsRes.data.agents);
    } catch (e) {
      setError('Gagal memuat data. Periksa koneksi ke server.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ── open dialog ──────────────────────────────────────────────────────────
  function openAdd() {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEdit(row) {
    setEditTarget(row);
    setForm({
      agent_id: row.agent_id ?? '',
      nama_jurnal: row.nama_jurnal || '',
      domain: row.domain || '',
      ip_public: row.ip_public || '',
      vip_fortiweb: row.vip_fortiweb || '',
      ip_private: row.ip_private || '',
      lewat_forti: row.lewat_forti || 'Ya',
      pointing_to_vip: row.pointing_to_vip || 'Sudah',
      update_ssl: row.update_ssl || 'Sudah',
      keterangan: row.keterangan || '',
    });
    setDialogOpen(true);
  }

  // ── auto-fill saat pilih agent ───────────────────────────────────────────
  function handleAgentChange(agentId) {
    if (agentId === 'none') {
      setForm((prev) => ({ ...prev, agent_id: '' }));
      return;
    }
    const ag = agents.find((a) => String(a.id) === String(agentId));
    if (ag) {
      setForm((prev) => ({
        ...prev,
        agent_id: ag.id,
        nama_jurnal: ag.alias || ag.server_source,
        ip_private: ag.ip_private,
      }));
    } else {
      setForm((prev) => ({ ...prev, agent_id: '' }));
    }
  }

  // ── simpan ───────────────────────────────────────────────────────────────
  async function handleSave() {
    if (!form.nama_jurnal.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, agent_id: form.agent_id || null };
      if (editTarget) {
        await fortiwebAPI.update(editTarget.id, payload);
      } else {
        await fortiwebAPI.create(payload);
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      alert('Gagal menyimpan: ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  }

  // ── hapus ────────────────────────────────────────────────────────────────
  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await fortiwebAPI.delete(deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      alert('Gagal menghapus: ' + (e.response?.data?.message || e.message));
    }
  }

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Breadcrumb + Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
            <span>Manajemen</span>
            <span>›</span>
            <span style={{ color: STATUS.primary.text }} className="font-medium">Konfigurasi FortiWeb</span>
          </div>
          <h1 className="text-base sm:text-lg font-semibold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" style={{ color: STATUS.primary.icon }} />
            Konfigurasi FortiWeb
          </h1>
          <p className="text-muted-foreground text-xs mt-0.5">
            Daftar server / jurnal yang traffic SSL-nya melewati firewall FortiWeb.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {isAdmin() && (
            <Button size="sm" onClick={openAdd}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Tambah
            </Button>
          )}
        </div>
      </div>

      {/* Search Bar + Filter Status */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative max-w-sm w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Cari nama jurnal, domain, IP..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-56">
            <SelectValue placeholder="Filter Status Sertifikat SSL" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">🔍 Semua Status</SelectItem>
            <SelectItem value="Sudah Inject Cert">
              <span className="flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: STATUS.valid.icon }} />
                Sudah Inject Cert
              </span>
            </SelectItem>
            <SelectItem value="Belum Inject Cert">
              <span className="flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: STATUS.critical.icon }} />
                Belum Inject Cert
              </span>
            </SelectItem>
            <SelectItem value="Cabut Cert">
              <span className="flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: STATUS.primary.icon }} />
                Cabut Cert
              </span>
            </SelectItem>
            <SelectItem value="no_status">
              <span className="flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: STATUS.expired.icon }} />
                Belum Dipilih
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
        {(filterStatus !== 'all' || search) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setSearch(''); setFilterStatus('all'); }}
            className="text-muted-foreground hover:text-foreground whitespace-nowrap"
          >
            ✕ Reset Filter
          </Button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-sm border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-xs text-destructive">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <LoadingSpinner text="Memuat data FortiWeb..." />
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
          <Server className="h-8 w-8 opacity-30" />
          <p className="text-xs">Belum ada data. Klik <strong>Tambah</strong> untuk mulai.</p>
        </div>
      ) : (
        <>
          {/* Tabel */}
          <div className="rounded-sm border overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr>
                  {[
                    'No', 'Nama Jurnal', 'Domain', 'IP Public Server',
                    'VIP FortiWeb', 'IP Private Server', 'Lewat Forti',
                    'Pointing to VIP', 'Update SSL', 'Status Sertifikat SSL',
                    ...(isAdmin() ? ['Aksi'] : []),
                  ].map((h) => (
                    <th
                      key={h}
                      className="h-9 px-3 text-left text-xs font-semibold text-muted-foreground whitespace-nowrap border-b"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center py-10 text-muted-foreground">
                      Tidak ada data yang cocok dengan pencarian "<strong>{search}</strong>".
                    </td>
                  </tr>
                ) : (
                  paginated.map((row, idx) => (
                    <tr
                      key={row.id}
                      className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                    >
                      <td className="px-3 py-2 text-muted-foreground font-medium">
                        {(currentPage - 1) * PER_PAGE + idx + 1}
                      </td>
                      <td className="px-3 py-2 font-medium">{row.nama_jurnal}</td>
                      <td className="px-3 py-2">
                        {row.domain ? (
                          <a
                            href={row.domain.startsWith('http') ? row.domain : `https://${row.domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {row.domain}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{row.ip_public || '-'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{row.vip_fortiweb || '-'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{row.ip_private || '-'}</td>
                      <td className="px-3 py-2">
                        <StatusBadgeLocal value={row.lewat_forti} positiveValues={['ya']} />
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadgeLocal value={row.pointing_to_vip} positiveValues={['sudah']} />
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadgeLocal value={row.update_ssl} positiveValues={['sudah']} />
                      </td>
                      <td className="px-3 py-2">
                        {row.keterangan === 'Sudah Inject Cert' ? (
                          <span
                            className="inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-bold whitespace-nowrap"
                            style={{ background: STATUS.valid.bg, borderColor: STATUS.valid.border, color: STATUS.valid.text }}
                          >
                            Sudah Inject Cert
                          </span>
                        ) : row.keterangan === 'Belum Inject Cert' ? (
                          <span
                            className="inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-bold whitespace-nowrap"
                            style={{ background: STATUS.critical.bg, borderColor: STATUS.critical.border, color: STATUS.critical.text }}
                          >
                            Belum Inject Cert
                          </span>
                        ) : row.keterangan === 'Cabut Cert' ? (
                          <span
                            className="inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-bold whitespace-nowrap"
                            style={{ background: STATUS.primary.bg, borderColor: STATUS.primary.border, color: STATUS.primary.text }}
                          >
                            Cabut Cert
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      {isAdmin() && (
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 hover:text-primary"
                              onClick={() => openEdit(row)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 hover:text-destructive"
                              onClick={() => setDeleteTarget(row)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-1">
            <p className="text-xs text-muted-foreground">
              Menampilkan{' '}
              <span className="font-medium text-foreground">
                {filtered.length === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1}–{Math.min(currentPage * PER_PAGE, filtered.length)}
              </span>{' '}
              dari <span className="font-medium text-foreground">{filtered.length}</span> data
              {search && <> (difilter dari <span className="font-medium">{rows.length}</span> total)</>}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setPage(1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <Button
                  key={p}
                  variant={p === currentPage ? 'default' : 'outline'}
                  size="icon"
                  className="h-8 w-8 text-xs"
                  onClick={() => setPage(p)}
                >
                  {p}
                </Button>
              ))}
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </>
      )}


      {/* ── Dialog Tambah / Edit ─────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" style={{ color: STATUS.primary.icon }} />
              {editTarget ? 'Edit Entri FortiWeb' : 'Tambah Entri FortiWeb'}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-1">
            {/* Pilih Agent */}
            <div className="col-span-2 space-y-1.5">
              <Label>Pilih Agent (Opsional — untuk auto-isi nama & IP Private)</Label>
              <Select
                value={form.agent_id ? String(form.agent_id) : 'none'}
                onValueChange={handleAgentChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="— Pilih agent atau isi manual —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Tanpa agent —</SelectItem>
                  {agents.map((a) => {
                    // Agent sudah dipakai jika ada di rows, kecuali agent yang sedang diedit
                    const isUsed = rows.some(
                      (r) => r.agent_id === a.id && r.id !== editTarget?.id
                    );
                    return (
                      <SelectItem
                        key={a.id}
                        value={String(a.id)}
                        disabled={isUsed}
                        className={isUsed ? 'opacity-40 cursor-not-allowed' : ''}
                      >
                        {a.alias || a.server_source} ({a.ip_private})
                        {isUsed && ' — sudah digunakan'}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Nama Jurnal */}
            <div className="space-y-1.5">
              <Label htmlFor="nama_jurnal">
                Nama Jurnal <span className="text-destructive">*</span>
              </Label>
              <Input
                id="nama_jurnal"
                placeholder="cth. Jurnal FTI"
                value={form.nama_jurnal}
                onChange={(e) => setForm({ ...form, nama_jurnal: e.target.value })}
              />
            </div>

            {/* Domain */}
            <div className="space-y-1.5">
              <Label htmlFor="domain">Domain</Label>
              <Input
                id="domain"
                placeholder="cth. jurnal.fti.umi.ac.id"
                value={form.domain}
                onChange={(e) => setForm({ ...form, domain: e.target.value })}
              />
            </div>

            {/* IP Public */}
            <div className="space-y-1.5">
              <Label htmlFor="ip_public">IP Public Server</Label>
              <Input
                id="ip_public"
                placeholder="cth. 103.133.36.151"
                value={form.ip_public}
                onChange={(e) => setForm({ ...form, ip_public: e.target.value })}
              />
            </div>

            {/* VIP FortiWeb */}
            <div className="space-y-1.5">
              <Label htmlFor="vip_fortiweb">VIP FortiWeb</Label>
              <Input
                id="vip_fortiweb"
                placeholder="cth. 192.168.2.101"
                value={form.vip_fortiweb}
                onChange={(e) => setForm({ ...form, vip_fortiweb: e.target.value })}
              />
            </div>

            {/* IP Private */}
            <div className="space-y-1.5">
              <Label htmlFor="ip_private">IP Private Server</Label>
              <Input
                id="ip_private"
                placeholder="cth. 192.168.168.9"
                value={form.ip_private}
                onChange={(e) => setForm({ ...form, ip_private: e.target.value })}
              />
            </div>

            {/* Lewat Forti */}
            <div className="space-y-1.5">
              <Label>Lewat Forti</Label>
              <Select
                value={form.lewat_forti}
                onValueChange={(v) => setForm({ ...form, lewat_forti: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ya">Ya</SelectItem>
                  <SelectItem value="Tidak">Tidak</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Pointing to VIP */}
            <div className="space-y-1.5">
              <Label>Pointing to VIP FortiWeb</Label>
              <Select
                value={form.pointing_to_vip}
                onValueChange={(v) => setForm({ ...form, pointing_to_vip: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Sudah">Sudah</SelectItem>
                  <SelectItem value="Belum">Belum</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Update SSL */}
            <div className="space-y-1.5">
              <Label>Update SSL</Label>
              <Select
                value={form.update_ssl}
                onValueChange={(v) => setForm({ ...form, update_ssl: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Sudah">Sudah</SelectItem>
                  <SelectItem value="Belum">Belum</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status Sertifikat SSL */}
            <div className="col-span-2 space-y-1.5">
              <Label>Status Sertifikat SSL</Label>
              <Select
                value={form.keterangan || 'none'}
                onValueChange={(v) => setForm({ ...form, keterangan: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="— Pilih status —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Belum dipilih —</SelectItem>
                  <SelectItem value="Sudah Inject Cert">
                    <span className="flex items-center gap-2">
                      <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: STATUS.valid.icon }} />
                      Sudah Inject Cert
                    </span>
                  </SelectItem>
                  <SelectItem value="Belum Inject Cert">
                    <span className="flex items-center gap-2">
                      <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: STATUS.critical.icon }} />
                      Belum Inject Cert
                    </span>
                  </SelectItem>
                  <SelectItem value="Cabut Cert">
                    <span className="flex items-center gap-2">
                      <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: STATUS.primary.icon }} />
                      Cabut Cert
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Batal
            </Button>
            <Button onClick={handleSave} disabled={saving || !form.nama_jurnal.trim()}>
              {saving ? 'Menyimpan...' : editTarget ? 'Simpan Perubahan' : 'Tambah Data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Dialog Konfirmasi Hapus ──────────────────────────────────────── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Entri?</AlertDialogTitle>
            <AlertDialogDescription>
              Data <strong>{deleteTarget?.nama_jurnal}</strong> akan dihapus secara permanen dan tidak
              dapat dikembalikan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              onClick={handleDelete}
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
